import { isNotDefined, keysIterator, makeValuesPredicate } from '@sgme/fp';
import { HStack, ServiceLoader, VStack } from '@sgme/ui';
// TODO: activate chatbot
// import type {
//   SgwtChatbotAdapter,
//   SgwtChatbotContext,
//   SgwtChatbotConversation,
//   SgwtChatbotMessage,
//   SgwtChatbotUserHistory,
//   SgwtChatbotWidgetPublishMessageHandler,
// } from '@sgwt/sgwt-widgets-react';
import { Fragment, type ReactNode, useCallback, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { useLoadedCurrencies } from '@/api/config/useLoadedCurrencies.ts';
import { ReceiveForceLogOffModal } from '@/api/signalR/messages/ReceiveForceLogOffModal.tsx';
import { useGetPinnedCompaniesQuery, useGetSharedTabListQuery, useWorkspaceQuery } from '@/api/workspace';
import { Blotter } from '@/App/Blotter';
import { BulkOrder } from '@/App/BulkOrder';
import { BulkTrade } from '@/App/BulkTrade/index.tsx';
import { Footer } from '@/App/Footer.tsx';
import TileGrid from '@/App/Grid';
import { GrowlList } from '@/App/Growl/GrowlList.tsx';
import { Header } from '@/App/Header';
import { Tabs } from '@/App/tabs/Tabs.tsx';
import { WorkspaceLockedBanner } from '@/App/tabs/WorkspaceLockedBanner.tsx';
import { VoiceTimestampingPanel } from '@/App/VoiceTimestamping/VoiceTimestampingPanel.tsx';
import { GlobalCatchAll } from '@/components/fatality/GlobalCatchAll.tsx';
import { NonReduxStateContextProvider } from '@/context/nonReduxStateContext';
import { UserProvider, useUserContext } from '@/context/UserContext.tsx';
import en from '@/locale/en.json';
import fr from '@/locale/fr.json';
import { useAppSelector } from '@/store/hooks.ts';
import { selectSelectedTabType } from '@/store/tabs/selectors.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { useSharedWorkspaceSignalrMessages } from '@/store/workspace/useSharedWorkspaceSignalrMessages.ts';

type SupportedLocale = 'en' | 'fr';
const locales: Record<SupportedLocale, Record<string, string>> = {
  en,
  fr: { ...en, ...fr },
};

const isSupportedLocale = makeValuesPredicate(keysIterator(locales));

export const App = () => {
  useWorkspaceQuery();
  useGetSharedTabListQuery();
  useGetPinnedCompaniesQuery();
  useSharedWorkspaceSignalrMessages();

  const [userLocale, setUserLocale] = useState<SupportedLocale>('en');

  const handleLanguageChanged = useCallback((locale: string) => {
    if (isNotDefined(locale) || !isSupportedLocale(locale)) {
      return;
    }

    const htmlTag = document.querySelector('html');
    if (htmlTag) {
      htmlTag.lang = locale;
    } else {
      console.error("'html' not found"); // can it really happen ?
    }

    setUserLocale(locale as SupportedLocale);
  }, []);

  return (
    <IntlProvider locale={userLocale} defaultLocale="en" messages={locales[userLocale]} textComponent={Fragment}>
      <GlobalCatchAll>
        <CurrenciesGate>
          <UserProvider>
            <NonReduxStateContextProvider>
              <div className="vh-100 d-flex flex-column bg-lvl4">
                <Header onLanguageChanged={handleLanguageChanged} />
                <InnerApp />
                <Footer />
              </div>

              <ReceiveForceLogOffModal />
            </NonReduxStateContextProvider>
          </UserProvider>
        </CurrenciesGate>
      </GlobalCatchAll>
    </IntlProvider>
  );
};

const InnerApp = () => {
  const tabType = useAppSelector(selectSelectedTabType);
  const tabId = useAppSelector(selectActiveTabId);

  const userContext = useUserContext();

  // TODO: activate chatbot
  // const { sgwtHelpCenter, sgwtChatbot } = useSgwtWidgets();

  // useEffect(() => {
  //   const myAdapter = new MyBotAdapter();
  //
  //   console.log('=== sgwtChatbot', sgwtChatbot);
  //
  //   if (sgwtChatbot !== undefined) {
  //     sgwtChatbot.setBotAdapter(myAdapter);
  //     console.log('=== chat bot adapter set ===');
  //   }
  //
  //   sgwtHelpCenter?.chat();
  // }, [sgwtHelpCenter, sgwtChatbot]);

  return (
    userContext !== null && (
      <>
        <Tabs />

        <WorkspaceLockedBanner />

        <VStack flex="expand" className="position-relative">
          <HStack flex="expand" className="flex-grow-1">
            {tabType === 'tiles' ? (
              <TileGrid className="flex-fill bg-lvl4" />
            ) : tabType === 'bulkOrder' ? (
              <BulkOrder />
            ) : tabType === 'bulkTrade' ? (
              <BulkTrade tabId={tabId} />
            ) : null}
            <VoiceTimestampingPanel />
          </HStack>
          <Blotter />
        </VStack>

        <GrowlList />
      </>
    )
  );
};

const CurrenciesGate = ({ children }: { children: ReactNode }) => {
  const isLoaded = useLoadedCurrencies();

  return <ServiceLoader isAppLoading={!isLoaded}>{children}</ServiceLoader>;
};

// TODO: activate chatbot
// export class MyBotAdapter implements SgwtChatbotAdapter {
//   // This handler is used to send messages asynchronously to the chat widget
//   private messageHandler: SgwtChatbotWidgetPublishMessageHandler | null = null;
//
//   constructor(context: SgwtChatbotContext) {
//     // ...
//   }
//
//   // This function is called by the <sgwt-chatbot> widget after its initialization, to allow the Adapter to send messages at any time...
//   public setPublishMessageHandler(handler: SgwtChatbotWidgetPublishMessageHandler) {
//     this.messageHandler = handler;
//   }
//
//   // Name of the adapter
//   get name(): string {
//     return 'my-bot-adapter';
//   }
//
//   private async callMyApi<TResponse>(data: any): Promise<TResponse> {
//     // Calling My Bot API
//     // ...
//   }
//
//   // This function is called by the <sgwt-chatbot> widget when the user sends a message.
//   public async userMessage(
//     userMessage: SgwtChatbotMessage,
//     context?: SgwtChatbotContext,
//   ): Promise<SgwtChatbotMessage | null> {
//     // Convert SgwtChatbotUserMessage to a format understandable by my API
//     const messageForMyApi = doSomeConversion(userMessage, context);
//     const apiResponse = await this.callMyApi(messageForMyApi);
//
//     // we can return nothing (maybe the response will come later)...
//     if (!apiResponse) {
//       return null;
//     }
//
//     // ...and send the response directly to the widget, converting the response to the SgwtChatbotMessage format.
//     return doSomeConversion(apiResponse);
//   }
//
//   // This function is called by the <sgwt-chatbot> when it needs to display the user history.
//   public async getHistory(context?: SgwtChatbotContext): Promise<SgwtChatbotUserHistory> {
//     // Call your API to retrieve the list following the format of SgwtChatbotUserHistory.
//   }
//
//   // This function is called by the <sgwt-chatbot> when it needs to get back to an existing conversation.
//   public async getConversation(
//     conversationId: string,
//     context?: SgwtChatbotContext,
//   ): Promise<SgwtChatbotConversation | null> {
//     const conversation = await this.callMyApi(`/conversations/${conversationId}`);
//     if (conversation === null) {
//       return null;
//     }
//     // Make some conversion...
//     return doSomeConversion(conversation);
//   }
// }
//
// function doSomeConversion(response: SgwtChatbotMessage, context?: SgwtChatbotContext): SgwtChatbotMessage {
//   console.log('=== doSomeConversion ===', response);
//
//   // This is just an example, you can do whatever conversion you want, as long as you return a valid SgwtChatbotMessage.
//   return response;
// }
